/**
 * Tooltip content model. Vega hands the tooltip handler whatever the `tooltip` encoding produced -
 * a record of field titles, a primitive, an array or a nested object - and this module turns it
 * into plain text rows.
 *
 * Nothing here produces markup. Vega's own tooltip plugin renders an HTML string and gives `title`
 * and `image` a special meaning, which turns an `image` field into an `<img src=...>` and a network
 * request; here both are ordinary values, so a definition that carries a URL can only ever display
 * that URL as text.
 */
export type VegaLiteTooltipRow = {
  label: string;
  value: string;
};

export type VegaLiteTooltipContent =
  | { status: 'hidden' }
  | { rows: VegaLiteTooltipRow[]; status: 'rows'; title: string | undefined }
  | { status: 'text'; text: string };

/** Matches the depth Vega's tooltip plugin uses before it collapses a nested object. */
const MAX_VALUE_DEPTH = 2;

function createDepthReplacer(maxDepth: number) {
  const stack: unknown[] = [];

  return function depthReplacer(this: unknown, _key: string, value: unknown): unknown {
    if (typeof value !== 'object' || value === null) {
      return value;
    }

    stack.length = stack.indexOf(this) + 1;

    if (stack.length > maxDepth) {
      return '[Object]';
    }

    if (stack.includes(value)) {
      return '[Circular]';
    }

    stack.push(value);

    return value;
  };
}

function stringifyBounded(value: unknown): string {
  try {
    return JSON.stringify(value, createDepthReplacer(MAX_VALUE_DEPTH)) ?? String(value);
  } catch {
    return String(value);
  }
}

function formatScalar(value: unknown): string {
  if (value === null) {
    return 'null';
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
  }

  if (typeof value === 'object') {
    return stringifyBounded(value);
  }

  return String(value);
}

function formatTitle(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value.length > 0 ? value : undefined;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getVegaLiteTooltipContent(value: unknown): VegaLiteTooltipContent {
  if (value === null || value === undefined || value === '') {
    return { status: 'hidden' };
  }

  if (Array.isArray(value)) {
    return { status: 'text', text: `[${value.map(formatScalar).join(', ')}]` };
  }

  if (value instanceof Date || !isRecord(value)) {
    return { status: 'text', text: formatScalar(value) };
  }

  const title = formatTitle(value.title);
  const rows = Object.entries(value)
    .filter(([key, entry]) => entry !== undefined && !(key === 'title' && title !== undefined))
    .map(([key, entry]) => ({ label: key, value: formatScalar(entry) }));

  if (rows.length === 0 && title === undefined) {
    return { status: 'text', text: '{}' };
  }

  return { rows, status: 'rows', title };
}
